import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, LineChart, Line, CartesianGrid
} from 'recharts';
import { generateMonthlyReport } from '../utils/generatePDF';
import { useAuth } from '../utils/AuthContext';
import { useLang } from '../utils/LanguageContext';
import { t } from '../utils/translations';
import './Analytics.css';
import Loader from '../components/Loader';

function Analytics() {
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [group, setGroup] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('week');

  const { lang } = useLang();
  const { org, isMax, branches } = useAuth();
  const tr = t[lang];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    const { data: ordersData } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: true });

    const { data: itemsData } = await supabase
      .from('order_items')
      .select('*');

    setOrders(ordersData || []);
    setItems(itemsData || []);

    if (branches.length > 1) {
      const { data: g } = await supabase.rpc('group_stats');
      setGroup(g || []);
    }
    setLoading(false);
  }

  // Max: download all orders + items of this branch as a CSV for Excel
  function exportCsv() {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['date', 'order', 'type', 'payment', 'product', 'qty', 'price', 'cost', 'line_total']];
    orders.forEach(o => {
      items.filter(i => i.order_id === o.id).forEach(i => {
        rows.push([
          new Date(o.created_at).toLocaleString('fr-MA', { timeZone: 'Africa/Casablanca' }),
          o.id.slice(-5).toUpperCase(),
          o.order_type || 'dine_in',
          o.payment_method,
          i.product_name,
          i.quantity,
          i.price,
          i.cost || 0,
          (parseFloat(i.price) * i.quantity).toFixed(2),
        ]);
      });
    });
    const csv = '﻿' + rows.map(r => r.map(esc).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `stratospos-${(org?.name || 'export').replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function isToday(str) {
    const orderDate = new Date(new Date(str).toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
    const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
    return orderDate.toDateString() === today.toDateString();
  }

  function toMoroccoDate(str) {
    return new Date(new Date(str).toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
  }

  const todayOrders = orders.filter(o => isToday(o.created_at));
  const todayRevenue = todayOrders.reduce((s, o) => s + parseFloat(o.total), 0);
  const totalRevenue = orders.reduce((s, o) => s + parseFloat(o.total), 0);
  const avgOrder = orders.length ? (totalRevenue / orders.length).toFixed(1) : 0;

  // Profit = revenue - cost, computed from the cost snapshot on each item
  const itemProfit = (i) => (parseFloat(i.price) - parseFloat(i.cost || 0)) * i.quantity;
  const totalProfit = items.reduce((s, i) => s + itemProfit(i), 0);
  const todayOrderIds = new Set(todayOrders.map(o => o.id));
  const todayProfit = items
    .filter(i => todayOrderIds.has(i.order_id))
    .reduce((s, i) => s + itemProfit(i), 0);
  const hasCostData = items.some(i => parseFloat(i.cost || 0) > 0);

  function getChartData() {
    const days = range === 'week' ? 7 : 30;
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
      d.setDate(d.getDate() - i);
      const label = d.toLocaleDateString('fr-MA', { day: '2-digit', month: 'short' });
      const revenue = orders
        .filter(o => toMoroccoDate(o.created_at).toDateString() === d.toDateString())
        .reduce((s, o) => s + parseFloat(o.total), 0);
      result.push({ label, revenue });
    }
    return result;
  }

  function getBestSellers() {
    const map = {};
    items.forEach(i => {
      if (!map[i.product_name]) map[i.product_name] = 0;
      map[i.product_name] += i.quantity;
    });
    return Object.entries(map)
      .map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 5);
  }

  function getHourlyData() {
    const map = {};
    orders.forEach(o => {
      const hour = toMoroccoDate(o.created_at).getHours();
      const label = `${hour}:00`;
      if (!map[label]) map[label] = 0;
      map[label] += parseFloat(o.total);
    });
    return Object.entries(map)
      .map(([label, revenue]) => ({ label, revenue }))
      .sort((a, b) => parseInt(a.label) - parseInt(b.label));
  }

  // Analytics is a Pro feature
  if (org?.plan === 'basic') {
    return (
      <div className="analytics">
        <div className="chart-card pro-upsell">
          <div className="pro-upsell-icon">📊✨</div>
          <h2>{tr.proOnly}</h2>
          <p>{tr.proOnlyMsg}</p>
        </div>
      </div>
    );
  }

  if (loading) return <Loader text={tr.loadingAnalytics} />;

  const chartData = getChartData();
  const bestSellers = getBestSellers();
  const hourlyData = getHourlyData();

  const monthName = new Date().toLocaleDateString('fr-MA', {
    month: 'long', timeZone: 'Africa/Casablanca'
  });
  const year = new Date().getFullYear();

  return (
    <div className="analytics">

      <div className="analytics-header">
        <h2 className="analytics-title">📊 {tr.analytics}</h2>
        <div className="analytics-actions">
          {isMax && (
            <button className="pdf-btn csv-btn" onClick={exportCsv}>{tr.exportCsv}</button>
          )}
          <button
            className="pdf-btn"
            onClick={() => generateMonthlyReport(orders, items, org?.name || 'StratosPOS', monthName, year, lang === 'ar' ? 'fr' : lang)}
          >
            {tr.pdfReport}
          </button>
        </div>
      </div>

      {isMax && group.length > 1 && (
        <div className="chart-card group-card">
          <span className="chart-title">🏢 {tr.groupView}</span>
          <div className="group-table">
            <div className="group-row group-head">
              <span>{tr.shopName}</span>
              <span>{tr.todayShort}</span>
              <span>{tr.thisMonth}</span>
            </div>
            {group.map(g => (
              <div className={`group-row ${g.org_id === org?.id ? 'current' : ''}`} key={g.org_id}>
                <span>{g.name}{g.org_id === org?.id ? ' ✓' : ''}</span>
                <span>{Number(g.today_revenue).toFixed(0)} {tr.currency}</span>
                <span>{Number(g.month_revenue).toFixed(0)} {tr.currency}</span>
              </div>
            ))}
            <div className="group-row group-total">
              <span>{tr.total}</span>
              <span>{group.reduce((s, g) => s + Number(g.today_revenue), 0).toFixed(0)} {tr.currency}</span>
              <span>{group.reduce((s, g) => s + Number(g.month_revenue), 0).toFixed(0)} {tr.currency}</span>
            </div>
          </div>
        </div>
      )}

      <div className="summary-grid">
        <div className="summary-card">
          <span className="summary-label">{tr.todayRevenueStat}</span>
          <span className="summary-value">{todayRevenue} {tr.currency}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{tr.todayOrdersStat}</span>
          <span className="summary-value">{todayOrders.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{tr.allTimeRevenue}</span>
          <span className="summary-value">{totalRevenue} {tr.currency}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{tr.avgOrderValue}</span>
          <span className="summary-value">{avgOrder} {tr.currency}</span>
        </div>
      </div>

      {hasCostData ? (
        <div className="profit-grid">
          <div className="profit-card">
            <span className="profit-label">{tr.todayProfit}</span>
            <span className="profit-value">{todayProfit.toFixed(0)} {tr.currency}</span>
          </div>
          <div className="profit-card">
            <span className="profit-label">{tr.totalProfit}</span>
            <span className="profit-value">{totalProfit.toFixed(0)} {tr.currency}</span>
          </div>
        </div>
      ) : (
        <div className="profit-hint">{tr.profitHint}</div>
      )}

      <div className="chart-card">
        <div className="chart-header">
          <span className="chart-title">{tr.revenue}</span>
          <div className="range-btns">
            <button className={range === 'week' ? 'active' : ''} onClick={() => setRange('week')}>{tr.days7}</button>
            <button className={range === 'month' ? 'active' : ''} onClick={() => setRange('month')}>{tr.days30}</button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => `${v} ${tr.currency}`} />
            <Bar dataKey="revenue" fill="#2093D0" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {bestSellers.length > 0 && (
        <div className="chart-card">
          <span className="chart-title">{tr.bestSellers}</span>
          <div className="best-sellers">
            {bestSellers.map((item, i) => (
              <div key={item.name} className="bs-row">
                <span className="bs-rank">#{i + 1}</span>
                <span className="bs-name">{item.name}</span>
                <div className="bs-bar-wrap">
                  <div
                    className="bs-bar"
                    style={{ width: `${(item.qty / bestSellers[0].qty) * 100}%` }}
                  />
                </div>
                <span className="bs-qty">{item.qty} {tr.sold}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hourlyData.length > 0 && (
        <div className="chart-card">
          <span className="chart-title">{tr.busiestHours}</span>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={hourlyData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${v} ${tr.currency}`} />
              <Line type="monotone" dataKey="revenue" stroke="#2093D0" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

    </div>
  );
}

export default Analytics;